import React, { useEffect, useRef, useState } from 'react';
import FullCalendar, {
	BusinessHoursInput,
	EventApi,
	EventClickArg,
	EventContentArg,
	EventInput,
} from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import moment, { Moment } from 'moment';
import { useAppContext } from '../../Services/AppContext';
import Tooltip from '../Popup/Tooltip';
import FullPageLoading from '../Utils/FullPageLoading/FullPageLoading';
import useTranslation from '../../Hooks/useTranslation';
import styles from './Calendar.module.scss';
import { CalendarView } from '../../Modules/Calendar/Enums/CalendarView.eunm';
import { CalendarViewIntervalEnum } from '../../Modules/Calendar/Enums/CalendarViewInterval.enum';
import CalendarCollapsedScheduleEvent from '../../Modules/Calendar/Components/CalendarCollapsedScheduleEvent/CalendarCollapsedScheduleEvent';
import CalendarDefaultEvent from '../../Modules/Calendar/Components/CalendarEvent/CalendarDefaultEvent';
import CalendarMonthViewEvent from '../../Modules/Calendar/Components/CalendarEvent/CalendarMonthViewEvent';
import CalendarEventTooltip from '../../Modules/Calendar/Components/CalendarEventTooltip/CalendarEventTooltip';
import { getMomentDate } from '../../Utils/dates.util';
import { CalendarEventTitle } from '../../Modules/Calendar/Enums/CalendarEventTitle.enum';
import { ServiceScheduleEventWorker } from '../../Modules/Workplace/Interfaces/ServiceScheduleEvent.interface';
import ECardFileViewer from '../../Modules/Ecard/Components/EcardFileViewer/ECardFileViewer';
import { ResourceInput } from '@fullcalendar/resource-common';

export interface CalendarSettings {
	showWeekends?: boolean;
	highlightWorkingTime?: boolean;
	slotMinTime?: string;
	slotMaxTime?: string;
	initialView?: string;
	forceMinimumEventHeight?: boolean;
	slotDurationDay?: string;
	slotDurationWeek?: string;
	slotDurationMonth?: string;
	defaultEventLabel?: CalendarEventTitle;
}

export interface CalendarResource extends ResourceInput {
	id: string;
	businessHours?: BusinessHoursInput;
}

interface ICalendar {
	onTimeClick?: (date: Moment, resourceId?: string) => void;
	onEventClick?: (event: EventClickArg) => void;
	onEventHover?: any;
	onEventMouseOut?: any;
	headerToolbar?: any;
	initialView?: string;
	events?: EventInput[];
	resources?: CalendarResource[];
	allDaySlot?: boolean;
	allDaySlotText?: string;
	businessHours?: BusinessHoursInput[];
	onDateChange?: (start: Moment, end: Moment) => void;
	editable?: boolean;
	onEventDrop?: (event: EventApi) => void;
	onEventResize?: (event: EventApi) => void;
	loadingEvents?: boolean;
	views?: any;
	moveToDate?: Date;
	showMyEvents?: boolean;
	setShowMyEvents?: React.Dispatch<React.SetStateAction<boolean>>;
	renderEventItem?: (event: any) => React.ReactElement;
	eventDidMount?: () => void;
	scrollToEventId?: string;
	settings?: CalendarSettings;
	onViewChange?: (view: CalendarView) => void;
	onPrint?: () => void;
}

export default function Calendar(props: ICalendar) {
	const { language } = useAppContext();
	const { t } = useTranslation();

	const {
		headerToolbar = null,
		initialView = null,
		onTimeClick,
		onEventClick,
		onEventMouseOut,
		onEventHover,
		events = [],
		resources = [],
		allDaySlot = false,
		allDaySlotText = t('base.shift'),
		businessHours,
		onDateChange,
		editable = false,
		onEventDrop,
		onEventResize,
		loadingEvents = false,
		views,
		moveToDate,
		showMyEvents,
		setShowMyEvents,
		renderEventItem,
		eventDidMount,
		scrollToEventId,
		settings,
		onViewChange,
		onPrint,
	} = props;

	const calendarRef = useRef<FullCalendar | null>(null);
	const calendarApi = calendarRef?.current?.getApi();
	const [showLoading, setShowLoading] = useState(false);
	const [isInitialized, setIsInitialized] = useState(true);
	const [slotDuration, setSlotDuration] = useState<string | undefined>(
		CalendarViewIntervalEnum.THIRTY_MINUTES,
	);
	const [calendarView, setCalendarView] = useState<CalendarView | undefined>(
		undefined,
	);
	const [fileId, setFileId] = useState<string | undefined>(undefined);

	const scrollToEvent = (eventId: string, attempts = 10, delay = 500) => {
		const attemptScroll = (remainingAttempts: number) => {
			const eventElement = document.querySelector(
				`[data-event-id="${eventId}"]`,
			);
			if (eventElement) {
				eventElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
				const harnessElement = eventElement.closest(
					'.fc-timegrid-event-harness',
				);
				if (harnessElement) {
					harnessElement.classList.add(styles.pulseEffect);
					setTimeout(() => {
						harnessElement.classList.remove(styles.pulseEffect);
					}, 2000);
				}
			} else if (remainingAttempts > 0) {
				setTimeout(() => attemptScroll(remainingAttempts - 1), delay);
			}
		};
		attemptScroll(attempts);
	};

	useEffect(() => {
		if (!loadingEvents) {
			setTimeout(() => setShowLoading(false), 300);
		} else {
			setShowLoading(true);
		}
	}, [loadingEvents]);

	const handleDateClick = (date: DateClickArg) => {
		const calendarApi = date.view.calendar;
		switch (date.view.type) {
			case 'timeGridDay':
			case 'timeGridWeek':
			case 'resourceTimeGridDay':
			case 'resourceTimeGridWeek':
				onTimeClick?.(moment(date.date), date.resource?.id);
				break;
			case 'dayGridMonth':
				calendarApi.changeView('timeGridDay', date.dateStr);
				break;
		}
	};

	const handleEventClick = (event: EventClickArg) => {
		if (!event.event.allDay) {
			onEventClick?.(event);
		}
	};

	const handleEventMouseOut = () => {
		onEventMouseOut?.();
	};

	const findPopupElement = (target: HTMLElement) => {
		const children = Array.from(target.children) as HTMLElement[];
		return children.find(child =>
			child.classList.contains('custom-event__popup'),
		);
	};

	const handleEventMouseOver = (e: React.MouseEvent<HTMLDivElement>) => {
		const popup = findPopupElement(e.currentTarget);
		if (popup) {
			popup.style.display = 'block';
		}
	};

	const handleEventMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
		const popup = findPopupElement(e.currentTarget);
		if (popup) {
			popup.style.display = 'none';
		}
	};

	const handleEventContent = (
		args: EventContentArg,
		calendarView?: CalendarView,
	) => {
		const event = args.event;

		if (renderEventItem) {
			return renderEventItem(event);
		}

		let eventNote = event.extendedProps?.note
			? event.extendedProps?.note.substring(0, 250)
			: null;
		if (eventNote && eventNote != event.extendedProps.note) {
			eventNote = eventNote.concat('...');
		}

		if (event.extendedProps.items) {
			return <CalendarCollapsedScheduleEvent event={event} />;
		}

		const workers = event.extendedProps.workers as
			| ServiceScheduleEventWorker[]
			| undefined;

		if (workers) {
			return (
				<div
					className={'office-hours flex-column'}
					style={{ backgroundColor: event.backgroundColor }}>
					<div className={'d-flex flex-column'}>
						{workers.map((item, index) => (
							<Tooltip
								key={index}
								position='bottom center'
								trigger={
									<div className='office-hours__name'>
										<div>{item.name}</div>
										<div>{event?.extendedProps?.shift?.timeInterval}</div>
									</div>
								}>
								<div>
									<div>
										<span>
											{item.role.map((item, index2) => {
												return (
													<React.Fragment key={index2}>
														{index2 > 0 && <>, </>}
														{item?.role?.name}
													</React.Fragment>
												);
											})}
											:
										</span>{' '}
										{item.name}
									</div>
									{event.extendedProps.shift && (
										<div className={'office-hours__role'}>
											{event?.extendedProps?.shift.label}
										</div>
									)}
								</div>
							</Tooltip>
						))}
						{event.extendedProps.workers.length < 1 && (
							<div className='office-hours__name'>
								{event?.extendedProps?.shift?.label}
							</div>
						)}
					</div>
				</div>
			);
		} else if (event.extendedProps.isHoliday) {
			return (
				<div style={{ padding: '5px 10px', fontSize: '12px' }}>
					{event.title}
				</div>
			);
		} else {
			let content = (
				<CalendarDefaultEvent
					event={event}
					handleEventMouseLeave={handleEventMouseLeave}
					handleEventMouseOver={handleEventMouseOver}
					displaySettings={{
						patientName:
							settings?.defaultEventLabel !== CalendarEventTitle.PATIENT_NAME,
					}}
				/>
			);

			if (calendarView === CalendarView.DAY_GRID_MONTH) {
				content = (
					<CalendarMonthViewEvent
						event={event}
						handleEventMouseLeave={handleEventMouseLeave}
						handleEventMouseOver={handleEventMouseOver}
					/>
				);
			}

			return (
				<Tooltip trigger={content} position='bottom center'>
					{close => (
						<CalendarEventTooltip
							event={event}
							eventNote={eventNote}
							onPreviewUploadedFile={(fileId: string) => {
								setFileId(fileId);
								close();
							}}
						/>
					)}
				</Tooltip>
			);
		}
	};

	const handleEventDidMount = (event: any) => {
		event.el.setAttribute('id', 'event-' + event.event.id);
		if (settings?.forceMinimumEventHeight) {
			event.el.style.minHeight = '45px';
		}
		eventDidMount?.();
	};

	const goToNextDay = () => {
		calendarApi?.next();
	};

	const goToPreviousDay = () => {
		calendarApi?.prev();
	};

	const handleDatesSet = (dateInfo: { startStr: string; endStr: string }) => {
		onDateChange?.(
			getMomentDate(dateInfo.startStr),
			getMomentDate(dateInfo.endStr),
		);
	};

	const handleEventDrop = (event: EventClickArg) => {
		onEventDrop?.(event.event);
	};

	const handleEventResize = (event: EventClickArg) => {
		onEventResize?.(event.event);
	};

	useEffect(() => {
		if (moveToDate) {
			calendarApi?.gotoDate(moveToDate);
			calendarApi?.changeView('timeGridDay');
			if (scrollToEventId) {
				scrollToEvent(scrollToEventId);
			}
		}
	}, [moveToDate, scrollToEventId]);

	const handleRedrawCalendar = () => {
		setIsInitialized(false);
		setIsInitialized(true);
	};

	useEffect(() => {
		const element = document.querySelector('#sidemenu-toggle');
		if (element) {
			element.addEventListener('click', handleRedrawCalendar);
			return () => {
				element.removeEventListener('click', handleRedrawCalendar);
			};
		}
	}, []);

	useEffect(() => {
		if (calendarApi?.view.type) {
			onViewChange?.(calendarApi.view.type as CalendarView);
			setCalendarView(calendarApi.view.type as CalendarView);
		}
	}, [calendarApi?.view]);

	useEffect(() => {
		if (calendarApi?.view.type && settings) {
			switch (calendarApi.view.type) {
				case CalendarView.TIME_GRID_DAY:
				case CalendarView.RESOURCE_TIME_GRID_DAY:
					if (settings?.slotDurationDay) {
						setSlotDuration(settings.slotDurationDay);
					}
					break;
				case CalendarView.TIME_GRID_WEEK:
				case CalendarView.RESOURCE_TIME_GRID_WEEK:
					if (settings?.slotDurationWeek) {
						setSlotDuration(settings.slotDurationWeek);
					}
					break;
				case CalendarView.DAY_GRID_MONTH:
					if (settings?.slotDurationMonth) {
						setSlotDuration(settings.slotDurationMonth);
					}
					break;
				default:
					setSlotDuration(CalendarViewIntervalEnum.THIRTY_MINUTES);
			}
		}
	}, [calendarApi?.view, settings]);

	let headerToolBarRight = 'timeGridDay,timeGridWeek,dayGridMonth';

	if (showMyEvents !== undefined && setShowMyEvents !== undefined) {
		headerToolBarRight = `showMyEvents,${headerToolBarRight}`;
	}

	if (onPrint) {
		headerToolBarRight += ',print';
	}

	if (!isInitialized) {
		return <></>;
	}

	return (
		<div className='fullCalendarStyles' style={{ height: '100%' }}>
			{showLoading && <FullPageLoading />}
			<FullCalendar
				ref={calendarRef}
				schedulerLicenseKey={'0784609858-fcs-1664878357'}
				plugins={[
					resourceTimeGridPlugin,
					dayGridPlugin,
					timeGridPlugin,
					interactionPlugin,
				]}
				weekends={settings?.showWeekends ?? undefined}
				initialView={settings?.initialView ?? initialView ?? 'dayGridMonth'}
				locale={language}
				slotDuration={slotDuration}
				slotLabelFormat={{
					hour: '2-digit',
					minute: '2-digit',
					omitZeroMinute: false,
				}}
				slotLabelInterval={{
					minutes: 1,
				}}
				headerToolbar={
					headerToolbar ?? {
						left: 'prev,next,today',
						center: 'title',
						right: headerToolBarRight,
					}
				}
				dateClick={handleDateClick}
				eventClick={handleEventClick}
				datesSet={handleDatesSet}
				allDaySlot={allDaySlot}
				allDayText={allDaySlotText}
				resources={resources}
				businessHours={
					settings?.highlightWorkingTime ? businessHours : undefined
				}
				events={events}
				eventShortHeight={0}
				firstDay={1}
				nowIndicator={true}
				buttonText={{
					today: t('base.shared.now'),
					month: t('base.months.one'),
					week: t('base.weeks.one'),
					day: t('base.days.one'),
					list: t('base.shared.list'),
				}}
				eventContent={args => handleEventContent(args, calendarView)}
				customButtons={{
					prev: { click: () => goToPreviousDay() },
					next: { click: () => goToNextDay() },
					showMyEvents: {
						text: showMyEvents
							? t('base.calendar.showAll')
							: t('base.calendar.showMine'),
						click: () => setShowMyEvents?.((prev: boolean) => !prev),
					},
					print: {
						text: t('component.calendar.customButtons.print'),
						click: () => onPrint?.(),
					},
				}}
				editable={editable}
				eventDrop={handleEventDrop}
				eventResize={handleEventResize}
				eventDidMount={handleEventDidMount}
				views={views}
				eventMouseLeave={handleEventMouseOut}
				slotMinTime={settings?.slotMinTime ?? '00:00:00'}
				slotMaxTime={settings?.slotMaxTime ?? '24:00:00'}
			/>
			{fileId && (
				<ECardFileViewer onClose={() => setFileId(undefined)} fileId={fileId} />
			)}
		</div>
	);
}
